import type { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import MuiDialog from '@mui/material/Dialog';
import type { ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { Panel } from '../Panel/index.ts';

/**
 * Dialog — a modal window for a focused task or decision, layered over the page with a backdrop
 * and focus trap.
 *
 * Thin wrapper over MUI Dialog whose surface (MUI's Paper) hosts the shared {@link Panel} chrome
 * (header + content + footer). All header/footer/spacing come from Panel, so Dialog and Drawer
 * stay visually consistent by construction. Only the surface (white, rd-4, centered, `maxWidth`)
 * is styled here, via the `MuiDialog.paper` slot — never the global MuiPaper.
 *
 * Wires accessibility automatically: `aria-labelledby` → title, `aria-describedby` → description.
 * Defaults to `fullWidth` so the dialog reaches its `maxWidth` (xs=444, sm=600, md=900, lg=1200,
 * xl=1536). Set `fullScreen` for an edge-to-edge, full-viewport dialog.
 *
 * SHARED primitives (Modal, Backdrop, Paper) are styled only via this Dialog's slots.
 */
export interface DialogProps extends Omit<MuiDialogProps, 'title'> {
  /** Heading (rendered `h2`, wired as `aria-labelledby`). */
  title?: ReactNode;
  /** Secondary line under the title (rendered `body1` / `text.secondary`, wired as `aria-describedby`). */
  description?: ReactNode;
  /** Primary footer actions — typically the primary `Button`. Rendered last in the group. */
  actions?: ReactNode;
  /** Low-emphasis footer actions (e.g. Cancel) — rendered before `actions`, on the same side. */
  secondaryActions?: ReactNode;
  /** Footer button-group side. Default `'right'` (Dialog convention); Drawer uses `'left'`. */
  footerAlign?: 'left' | 'right';
  /** Show the ✕ close button in the header. Default `true` (only rendered when `onClose` is set). */
  showCloseButton?: boolean;
  /** Accessible label for the close button. Default `"Close"`. */
  closeButtonLabel?: string;
  /** Show the divider below the header. Default `true`. */
  headerDivider?: boolean;
  /** Show the divider above the footer. Default `true`. */
  footerDivider?: boolean;
}

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    title,
    description,
    actions,
    secondaryActions,
    footerAlign = 'right',
    children,
    showCloseButton = true,
    closeButtonLabel = 'Close',
    headerDivider = true,
    footerDivider = true,
    onClose,
    fullWidth = true,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref
) {
  const generatedId = useId();
  const titleId = title ? (ariaLabelledBy ?? `${generatedId}-title`) : ariaLabelledBy;
  const descId = description ? (ariaDescribedBy ?? `${generatedId}-description`) : ariaDescribedBy;

  return (
    <MuiDialog
      ref={ref}
      onClose={onClose}
      fullWidth={fullWidth}
      aria-labelledby={titleId}
      aria-describedby={descId}
      {...props}
    >
      <Panel
        title={title}
        description={description}
        actions={actions}
        secondaryActions={secondaryActions}
        footerAlign={footerAlign}
        showCloseButton={showCloseButton}
        closeButtonLabel={closeButtonLabel}
        headerDivider={headerDivider}
        footerDivider={footerDivider}
        titleId={titleId}
        descriptionId={descId}
        // Close is an explicit dismissal (like Esc), not a backdrop click — so a consumer that
        // filters out `backdropClick` to disable outside-close still closes via the ✕.
        onClose={onClose ? event => onClose(event, 'escapeKeyDown') : undefined}
      >
        {children}
      </Panel>
    </MuiDialog>
  );
});
